import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Plane, Upload, CheckCircle2, AlertCircle, Loader2, Shield, Clock, FileCheck, Star, Building2, MapPin, Camera, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import SamplePDFPreview from '../components/SamplePDFPreview';
import PromoVideo from '../components/home/PromoVideo';
import SearchableSelect from '../components/SearchableSelect';
import LanguageSelector from '../components/LanguageSelector';
import FAQSection from '../components/FAQSection';
import StickyCTAMobile from '../components/StickyCTAMobile';
import soundManager from '../utils/sounds';
import CopySections, { FOOTER_DISCLAIMER } from '../components/home/CopySections';
import { track, trackOnce, getGaClientId, PREFILL_KEY } from '../utils/track';
import { MeasurementsForm, EMPTY_MEASUREMENTS, measurementsComplete, toCmPayload } from '../components/home/MeasurementsForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macau' },
  { code: 'MK', name: 'Macedonia' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion Island (France)' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SZ', name: 'Swaziland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
];


// Reviews Carousel Component
function ReviewsCarousel() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          axios.get(`${API}/reviews?limit=6`),
          axios.get(`${API}/reviews/stats`)
        ]);
        setReviews(reviewsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.log('Could not fetch reviews');
      }
    };
    fetchReviews();
  }, []);

  if (reviews.length < 3) return null;
  const displayReviews = reviews;
  const displayStats = stats;

  return (
    <section data-testid="reviews-section" className="py-16 bg-gradient-to-b from-slate-100 to-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{t('reviews.title')}</h2>
        <p className="text-slate-600">{t('reviews.subtitle')}</p>
      </div>
      {/* Stats */}
      <div className="flex justify-center items-center gap-6 mb-8">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-5 h-5 ${star <= Math.round(displayStats.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          ))}
        </div>
        <span className="text-lg font-semibold text-slate-900">
          {displayStats.average_rating.toFixed(1)} / 5
        </span>
        <span className="text-slate-500">
          ({displayStats.total_reviews} {displayStats.total_reviews === 1 ? 'review' : 'reviews'})
        </span>
      </div>

      {/* Reviews Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {displayReviews.slice(0, 3).map((review) => (
          <div
            key={review.id}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                />
              ))}
            </div>
            <p className="text-slate-700 text-sm mb-4 italic">"{review.comment}"</p>
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-900">{review.name}</span>
              {review.verified && (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
    </section>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const wizardRef = useRef(null);
  useEffect(() => {
    if (window.location.hash === '#check') setTimeout(() => wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, []);

  // Smart default: flight date = today + 30 days
  const getDefaultFlightDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  // Step 1: Flight details
  const [flightDate, setFlightDate] = useState(getDefaultFlightDate());
  const [originCountry, setOriginCountry] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [airline, setAirline] = useState('');
  const [transits, setTransits] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [petType, setPetType] = useState('');
  const [petBreed, setPetBreed] = useState('');
  const [measurements, setMeasurements] = useState(EMPTY_MEASUREMENTS);

  // Pet breeds data
  const DOG_BREEDS = [
    { slug: 'french-bulldog', name: 'French Bulldog', category: 'brachycephalic' },
    { slug: 'english-bulldog', name: 'English Bulldog', category: 'brachycephalic' },
    { slug: 'pug', name: 'Pug', category: 'brachycephalic' },
    { slug: 'boston-terrier', name: 'Boston Terrier', category: 'brachycephalic' },
    { slug: 'boxer', name: 'Boxer', category: 'brachycephalic-mild' },
    { slug: 'shih-tzu', name: 'Shih Tzu', category: 'brachycephalic' },
    { slug: 'cavalier-king-charles', name: 'Cavalier King Charles Spaniel', category: 'brachycephalic-mild' },
    { slug: 'golden-retriever', name: 'Golden Retriever', category: 'standard' },
    { slug: 'labrador-retriever', name: 'Labrador Retriever', category: 'standard' },
    { slug: 'german-shepherd', name: 'German Shepherd', category: 'large' },
    { slug: 'beagle', name: 'Beagle', category: 'standard' },
    { slug: 'border-collie', name: 'Border Collie', category: 'standard' },
    { slug: 'dachshund', name: 'Dachshund', category: 'small' },
    { slug: 'poodle', name: 'Poodle', category: 'standard' },
    { slug: 'chihuahua', name: 'Chihuahua', category: 'small' },
    { slug: 'yorkshire-terrier', name: 'Yorkshire Terrier', category: 'small' },
    { slug: 'husky', name: 'Siberian Husky', category: 'large' },
    { slug: 'other-dog', name: 'Other Dog Breed', category: 'other' },
  ];

  const CAT_BREEDS = [
    { slug: 'persian-cat', name: 'Persian Cat', category: 'brachycephalic' },
    { slug: 'british-shorthair', name: 'British Shorthair', category: 'brachycephalic-mild' },
    { slug: 'maine-coon', name: 'Maine Coon', category: 'large' },
    { slug: 'siamese', name: 'Siamese Cat', category: 'standard' },
    { slug: 'other-cat', name: 'Other Cat Breed', category: 'other' },
  ];

  // Step 2: Plan selection
  const [selectedPlan, setSelectedPlan] = useState('');

  // Step 3: File uploads
  const [photoA, setPhotoA] = useState(null);
  const [photoB, setPhotoB] = useState(null);
  const [photoC, setPhotoC] = useState(null);
  const [docPassport, setDocPassport] = useState(null);
  const [docRabies, setDocRabies] = useState(null);
  const [docHealth, setDocHealth] = useState(null);
  const [docImportPermit, setDocImportPermit] = useState(null);
  const [docBloodTest, setDocBloodTest] = useState(null);
  const [docParasite, setDocParasite] = useState(null);
  const [docMicrochip, setDocMicrochip] = useState(null);

  // Step 4: Email
  const [email, setEmail] = useState('');

  // Geolocation
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoDetected, setGeoDetected] = useState(false);

  const detectLocation = async () => {
    setGeoLoading(true);
    try {
      // Try multiple IP geolocation APIs
      let countryCode = null;
      
      // Try ip-api.com first (no API key needed)
      try {
        const response = await fetch('http://ip-api.com/json/?fields=countryCode');
        const data = await response.json();
        if (data.countryCode) countryCode = data.countryCode;
      } catch (e) {
        // Fallback to ipinfo.io
        try {
          const response = await fetch('https://ipinfo.io/json?token=');
          const data = await response.json();
          if (data.country) countryCode = data.country;
        } catch (e2) {
          // Last fallback: cloudflare
          try {
            const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
            const text = await response.text();
            const match = text.match(/loc=([A-Z]{2})/);
            if (match) countryCode = match[1];
          } catch (e3) {
            console.error('All geolocation APIs failed');
          }
        }
      }
      
      if (countryCode) {
        const country = COUNTRIES.find(c => c.code === countryCode);
        if (country) {
          setOriginCountry(countryCode);
          setGeoDetected(true);
          toast.success(`Location detected: ${country.name}`);
        } else {
          toast.error(t('toast.geo_nomatch'));
        }
      } else {
        toast.error(t('toast.geo_fail'));
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      toast.error(t('toast.geo_fail'));
    }
    setGeoLoading(false);
  };

  useEffect(() => {
    createSession();
    fetchAirlines();
  }, []);

  const scrollToWizard = () => {
    setTimeout(() => {
      if (wizardRef.current) {
        wizardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Play complete sound when all 3 photos are uploaded
  useEffect(() => {
    if (photoA && photoB && photoC) {
      soundManager.playComplete();
    }
  }, [photoA, photoB, photoC]);

  useEffect(() => {
    const breed = (petType === 'dog' ? DOG_BREEDS : CAT_BREEDS).find(b => b.slug === petBreed);
    if (!petBreed) return;
    setMeasurements(m => ({ ...m, brachycephalic: !!breed && breed.category.startsWith('brachycephalic') }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petBreed, petType]);

  useEffect(() => {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    sessionStorage.removeItem(PREFILL_KEY);
    if (p.petType) setPetType(p.petType);
    setMeasurements(m => ({ ...m, unit: p.unit, a: p.a, b: p.b, c: p.c, d: p.d, bedding: p.bedding, brachycephalic: p.brachycephalic, animals: p.animals, prefilled: true }));
    setTimeout(() => wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, []);

  const createSession = async () => {
    try {
      const response = await axios.post(`${API}/session`);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error(t('toast.session_fail'));
    }
  };

  const fetchAirlines = async () => {
    try {
      const response = await axios.get(`${API}/airlines`);
      setAirlines(response.data);
    } catch (error) {
      console.error('Failed to fetch airlines:', error);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!flightDate || !originCountry || !destinationCountry) {
        toast.error(t('toast.flight_missing'));
        return;
      }
      if (originCountry === destinationCountry) {
        toast.error(t('toast.same_country'));
        return;
      }
      if (!petType || !petBreed) {
        toast.error(t('toast.pet_missing'));
        return;
      }
    } else if (step === 2) {
      if (!selectedPlan) {
        toast.error(t('toast.plan_missing'));
        return;
      }
    } else if (step === 3) {
      if (!measurementsComplete(measurements)) {
        toast.error(t('measure.missing'));
        return;
      }
      if (!photoA || !photoB || !photoC) {
        toast.error(t('toast.photos_missing'));
        return;
      }
      if (selectedPlan === '$89' && ((!docMicrochip && !docPassport) || !docRabies)) {
        toast.error(t('toast.docs_missing'));
        return;
      }
    }
    
    if (step === 1) track('form_step_info', { origin: originCountry, destination: destinationCountry });
    if (step === 3) track('form_step_upload', { plan: selectedPlan });
    setStep(step + 1);
    soundManager.playStepComplete(); // Play sound on step progression
    scrollToWizard();
  };

  const handleBack = () => {
    setStep(step - 1);
    scrollToWizard();
  };

  const handleSubmit = async () => {
    if (!email) {
      toast.error(t('toast.email_missing'));
      return;
    }

    if (!sessionId) {
      toast.error(t('toast.session_expired'));
      return;
    }

    track('checkout_start', { plan: selectedPlan, value: selectedPlan === '$89' ? 89 : 39, currency: 'USD' });

    setLoading(true);

    try {
      const submissionResponse = await axios.post(`${API}/submission`, {
        session_id: sessionId,
        email: email,
        flight_date: flightDate,
        origin_country: originCountry,
        destination_country: destinationCountry,
        airline: airline,
        transits: transits,
        plan_selected: selectedPlan,
        pet_type: petType,
        pet_breed: petBreed,
        measurements: toCmPayload(measurements),
        language: i18n.language // Include user's language for PDF translation
      });

      const subId = submissionResponse.data.submission_id;

      const formData = new FormData();
      formData.append('submission_id', subId);
      if (photoA) formData.append('photo_a', photoA);
      if (photoB) formData.append('photo_b', photoB);
      if (photoC) formData.append('photo_c', photoC);
      if (docPassport) formData.append('doc_passport', docPassport);
      if (docRabies) formData.append('doc_rabies', docRabies);
      if (docHealth) formData.append('doc_health', docHealth);
      if (docImportPermit) formData.append('doc_import_permit', docImportPermit);
      if (docBloodTest) formData.append('doc_blood_test', docBloodTest);
      if (docParasite) formData.append('doc_parasite', docParasite);
      if (docMicrochip) formData.append('doc_microchip', docMicrochip);

      await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const checkoutResponse = await axios.post(`${API}/create-checkout`, {
        submission_id: subId,
        email: email,
        ga_client_id: getGaClientId()
      });

      window.location.href = checkoutResponse.data.checkout_url;

    } catch (error) {
      console.error('Submission error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show more specific error message
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message ||
                          'Failed to process submission. Please try again.';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const FileUploadZone = ({ file, setFile, label, description }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
      maxFiles: 1,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          setFile(acceptedFiles[0]);
          soundManager.playUpload(); // Play sound on successful upload
        }
      }
    });

    return (
      <div
        {...getRootProps()}
        data-testid={`upload-zone-${label.toLowerCase().replace(/\s/g, '-')}`}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-[#0066CC] bg-blue-50' : 'border-slate-200 hover:border-[#0066CC]'
        } ${file ? 'bg-green-50 border-green-400' : ''}`}
      >
        <input {...getInputProps()} />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
          </div>
        ) : (
          <div>
            <Upload className="w-6 h-6 mx-auto mb-1 text-slate-400" />
            <p className="text-sm font-medium text-slate-700">{label}</p>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        )}
      </div>
    );
  };

  // Step labels will be translated
  const getStepLabels = () => [
    t('wizard.step1_title').split(' ')[0], // "Flight" / "Vol" / etc.
    t('wizard.step2_title').split(' ')[0], // "Plan" / "Formule" / etc.
    t('upload.title').split(' ')[0], // "Upload" / "Téléchargez" / etc.
    t('payment.secure_payment').split(' ')[0] // "Pay" / "Paiement" / etc.
  ];
  
  const StepIndicator = () => {
    const stepLabels = getStepLabels();
    return (
    <div className="mb-8">
      {/* Progress bar */}
      <div className="relative mb-4">
        <div className="h-2 bg-slate-100 rounded-full">
          <div 
            className="h-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
            style={{ width: `${((step - 1) / 3) * 100}%` }}
          />
        </div>
      </div>
      {/* Step circles with labels */}
      <div className="flex items-center justify-between">
        {[1, 2, 3, 4].map((num) => (
          <div key={num} className="flex flex-col items-center">
            <div
              data-testid={`step-indicator-${num}`}
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step > num
                  ? 'bg-green-500 text-white'
                  : step === num
                  ? 'bg-[#0066CC] text-white ring-4 ring-blue-100'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              {step > num ? '✓' : num}
            </div>
            <span className={`text-xs mt-1.5 font-medium ${
              step >= num ? 'text-[#0066CC]' : 'text-slate-400'
            }`}>
              {stepLabels[num - 1]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <html lang={i18n.resolvedLanguage === 'fr' ? 'fr' : 'en'} />
        <title>{i18n.resolvedLanguage === 'fr' ? 'Vérification IATA de la caisse de votre animal | PetFlyCheck' : 'IATA Pet Crate Check for Dogs & Cats | PetFlyCheck'}</title>
        <meta name="description" content={i18n.resolvedLanguage === 'fr' ? "Vérifiez la caisse de votre chien ou chat selon les règles IATA avant le vol : formule de taille officielle, contrôle photo, rapport PDF. Dès 39 $." : "Check your pet's travel crate against IATA rules before you fly. Size via the official formula, 3-photo visual check, PDF report. From $39."} />
        <link rel="canonical" href={i18n.resolvedLanguage === 'fr' ? 'https://petflycheck.io/fr' : 'https://petflycheck.io/'} />
        <link rel="alternate" hrefLang="en" href="https://petflycheck.io/" />
        <link rel="alternate" hrefLang="fr" href="https://petflycheck.io/fr" />
        <link rel="alternate" hrefLang="x-default" href="https://petflycheck.io/" />
      </Helmet>
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between gap-2">
            <a href="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img 
                src="/logo.png" 
                alt="PetFlyCheck" 
                className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
              />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 hidden xs:inline">PetFlyCheck</span>
            </a>
            <nav className="flex items-center gap-1.5 sm:gap-3 md:gap-5">
              <a href="/pro" className="flex items-center gap-1 px-2 sm:px-2.5 py-1 sm:py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-[10px] sm:text-xs font-medium rounded-full hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm whitespace-nowrap">
                <Building2 className="w-3 h-3 flex-shrink-0" />
                <span className="hidden sm:inline">{t('nav.pro')}</span>
                <span className="sm:hidden">Pro</span>
              </a>
              <a href="/faq" className="text-xs sm:text-sm md:text-base font-medium text-slate-600 hover:text-[#0066CC] transition-colors">{t('nav.faq')}</a>
              <a href="/support" className="hidden md:block text-sm md:text-base font-medium text-slate-600 hover:text-[#0066CC] transition-colors">{t('nav.support')}</a>
              <LanguageSelector />
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Only on Step 1 */}
      {step === 1 && (
        <section className="relative bg-gradient-to-b from-sky-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left: Text Content */}
              <div className="text-center md:text-left">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 leading-tight">
                    {t('hero.title')}
                  </h1>
                  <p className="text-base md:text-lg text-slate-600 mb-6 max-w-lg mx-auto md:mx-0">
                    {t('hero.subtitle')}
                  </p>
                  
                  <ul data-testid="hero-benefits" className="space-y-2 mb-5 text-sm text-slate-700 text-left max-w-lg mx-auto md:mx-0">
                    {['b1', 'b2', 'b3'].map(k => (
                      <li key={k} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span><strong>{t(`hero.${k}_title`)}</strong> {t(`hero.${k}_text`)}</span></li>
                    ))}
                  </ul>
                  <div className="mb-4 text-center md:text-left">
                    <Button data-testid="hero-cta-btn" onClick={() => wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full h-12 px-6 text-base font-semibold">
                      {t('hero.cta_button')}
                    </Button>
                    <a href="/guides/iata-crate-size-calculator" data-testid="hero-calculator-link" className="block md:inline md:ml-4 mt-2 text-sm text-blue-700 font-medium italic">
                      {t('hero.calculator_link')}
                    </a>
                    <p className="text-xs text-slate-500 mt-2 max-w-lg">{t('hero.cta_microcopy')}</p>
                  </div>
                  {/* Guarantee + Stripe Badge */}
                  <div className="flex items-center justify-center md:justify-start gap-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200/60">
                    <div className="flex items-center gap-2">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_1074c80d-856e-47c6-a57b-1b698a3685a0/artifacts/sqlsyi0v_stripe.svg" 
                        alt="Stripe" 
                        className="h-6 w-auto"
                      />
                      <span className="text-xs text-slate-500">{t('hero.secure_payment')}</span>
                    </div>
                  </div>
                  
                  <p data-testid="independent-disclaimer" className="text-xs text-slate-500 mt-3 text-center md:text-left">
                    {t('disclaimer.independent')}
                  </p>
                </motion.div>
              </div>
              
              {/* Right: Hero Image */}
              <motion.div
                initial={{ scale: 0.97 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-300/50">
                  <img
                    data-testid="hero-image"
                    src="/hero-checkin-1600.webp"
                    srcSet="/hero-checkin-800.webp 800w, /hero-checkin-1600.webp 1600w"
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    width="1600"
                    height="900"
                    fetchpriority="high"
                    decoding="async"
                    alt="Woman checking in her golden retriever in an airline crate at an airport check-in counter"
                    className="w-full h-64 md:h-80 lg:h-96 object-cover object-[100%_50%]"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Demo Video Section - Only on Step 1 */}
      {step === 1 && (
      <section className="py-8 md:py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-6"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              {t('how_it_works.title')}
            </h2>
            <p className="text-slate-600">
              {t('how_it_works.subtitle')}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-300/50 bg-slate-900"
          >
            <PromoVideo />
          </motion.div>
        </div>
      </section>
      )}

      {/* Wizard Section */}
      <section ref={wizardRef} className="py-8 md:py-12">
        <div className="max-w-xl mx-auto px-4">
          <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
            <CardContent className="p-6 md:p-8">
              <StepIndicator />
              
              {/* Sound toggle button */}
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={() => {
                    const newState = soundManager.toggle();
                    setSoundEnabled(newState);
                  }}
                  className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  title={soundEnabled ? t('sound.on') : t('sound.off')}
                >
                  {soundEnabled ? (
                    <Volume2 className="w-3.5 h-3.5" />
                  ) : (
                    <VolumeX className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">{soundEnabled ? t('sound.on') : t('sound.off')}</span>
                </button>
              </div>
              
              {/* Quick link to sample */}
              {step === 1 && (
                <div className="flex justify-center mb-4">
                  <button 
                    type="button"
                    onClick={() => {
                      document.querySelector('[data-testid="sample-pdf-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <FileCheck className="w-4 h-4" />
                    {t('sample_report.title')}
                  </button>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-slate-900">
                  {step === 1 && t('wizard.step1_title')}
                  {step === 2 && t('wizard.step2_title')}
                  {step === 3 && t('upload.title')}
                  {step === 4 && t('wizard.step4_title')}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {step === 1 && t('wizard.step1_subtitle')}
                  {step === 2 && t('wizard.step2_subtitle')}
                  {step === 3 && t('upload.subtitle')}
                  {step === 4 && t('wizard.step4_subtitle')}
                </p>
                {/* Time estimate & Social proof */}
                {step === 1 && (
                  <div className="flex items-center justify-center gap-4 mt-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      ~2 min
                    </span>
                  </div>
                )}
              </div>

              {/* Step 1: Flight Details */}
              {step === 1 && (
                <motion.div
                  onFocusCapture={() => trackOnce('form_start')}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <div>
                    <Label htmlFor="flight-date" className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('form.travel_date')} *
                    </Label>
                    <Input
                      id="flight-date"
                      data-testid="flight-date-input"
                      type="date"
                      value={flightDate}
                      onChange={(e) => setFlightDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="h-11 bg-slate-50 border-slate-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium text-slate-700">
                        {t('form.origin_country')} *
                      </Label>
                      {!originCountry && !geoLoading && (
                        <button
                          type="button"
                          onClick={detectLocation}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <MapPin className="w-3 h-3" />
                          {t('form.use_location')}
                        </button>
                      )}
                      {geoLoading && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          ...
                        </span>
                      )}
                      {geoDetected && originCountry && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          ✓
                        </span>
                      )}
                    </div>
                    <SearchableSelect
                      value={originCountry}
                      onValueChange={(val) => { setOriginCountry(val); setGeoDetected(false); }}
                      options={COUNTRIES}
                      placeholder={t('form.search_origin')}
                      testId="origin-country-select"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('form.destination_country')} *
                    </Label>
                    <SearchableSelect
                      value={destinationCountry}
                      onValueChange={setDestinationCountry}
                      options={COUNTRIES.filter(c => c.code !== originCountry)}
                      placeholder={t('form.search_destination')}
                      testId="destination-country-select"
                    />
                    {destinationCountry && destinationCountry === originCountry && (
                      <p className="text-red-500 text-xs mt-1">{t('errors.dest_different')}</p>
                    )}
                  </div>

                  {/* Popular Routes - Quick Selection */}
                  {!originCountry && !destinationCountry && (
                    <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-4 border border-blue-100">
                      <p className="text-xs font-medium text-slate-600 mb-2.5">{t('form.popular_routes')}</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { from: 'FR', to: 'US', label: '🇫🇷 → 🇺🇸' },
                          { from: 'GB', to: 'AU', label: '🇬🇧 → 🇦🇺' },
                          { from: 'DE', to: 'ES', label: '🇩🇪 → 🇪🇸' },
                          { from: 'US', to: 'GB', label: '🇺🇸 → 🇬🇧' },
                          { from: 'FR', to: 'CA', label: '🇫🇷 → 🇨🇦' },
                        ].map((route) => (
                          <button
                            key={`${route.from}-${route.to}`}
                            type="button"
                            onClick={() => {
                              setOriginCountry(route.from);
                              setDestinationCountry(route.to);
                              setGeoDetected(false);
                            }}
                            className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          >
                            {route.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('form.transit_countries')}
                    </Label>
                    <div className="space-y-2">
                      {transits.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                          {transits.map((code) => {
                            const country = COUNTRIES.find(c => c.code === code);
                            return (
                              <span
                                key={code}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#0066CC]/10 text-[#0066CC] rounded-full text-xs font-medium"
                              >
                                {country?.name}
                                <button
                                  onClick={() => setTransits(transits.filter(t => t !== code))}
                                  className="hover:bg-[#0066CC]/20 rounded-full p-0.5"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (value && !transits.includes(value)) {
                            setTransits([...transits, value]);
                          }
                        }}
                      >
                        <SelectTrigger data-testid="transit-country-select" className="h-10 bg-slate-50 border-slate-200 rounded-lg text-sm">
                          <SelectValue placeholder={t('form.add_transit')} />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES
                            .filter(country => !transits.includes(country.code) && country.code !== originCountry && country.code !== destinationCountry)
                            .map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('form.airline')}
                    </Label>
                    <Select value={airline} onValueChange={setAirline}>
                      <SelectTrigger data-testid="airline-select" className="h-11 bg-slate-50 border-slate-200 rounded-lg">
                        <SelectValue placeholder={t('form.select_airline')} />
                      </SelectTrigger>
                      <SelectContent>
                        {airlines.map((a, idx) => (
                          <SelectItem key={idx} value={a.airline_name}>
                            {a.airline_name} ({a.airline_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pet Type Selection */}
                  <div className="pt-4 border-t border-slate-200">
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('form.pet_type')} *
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        data-testid="pet-type-dog"
                        onClick={() => { setPetType('dog'); setPetBreed(''); }}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          petType === 'dog' 
                            ? 'border-[#0066CC] bg-[#0066CC]/5' 
                            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                        }`}
                      >
                        <img 
                          src="/dog-petflycheck.png" 
                          alt="Dog" 
                          className="w-32 h-32 sm:w-36 sm:h-36 object-cover rounded-xl shadow-md"
                        />
                        <span className={`font-medium ${petType === 'dog' ? 'text-[#0066CC]' : 'text-slate-700'}`}>{t('form.dog')}</span>
                      </button>
                      <button
                        type="button"
                        data-testid="pet-type-cat"
                        onClick={() => { setPetType('cat'); setPetBreed(''); }}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          petType === 'cat' 
                            ? 'border-[#0066CC] bg-[#0066CC]/5' 
                            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                        }`}
                      >
                        <img 
                          src="/cat-petflycheck.png" 
                          alt="Cat" 
                          className="w-32 h-32 sm:w-36 sm:h-36 object-cover rounded-xl shadow-md"
                        />
                        <span className={`font-medium ${petType === 'cat' ? 'text-[#0066CC]' : 'text-slate-700'}`}>{t('form.cat')}</span>
                      </button>
                    </div>
                  </div>

                  {/* Breed Selection - Appears after pet type is selected */}
                  {petType && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">
                        {petType === 'dog' ? t('form.select_dog_breed') : t('form.select_cat_breed')} *
                      </Label>
                      <Select value={petBreed} onValueChange={setPetBreed}>
                        <SelectTrigger data-testid="pet-breed-select" className="h-11 bg-slate-50 border-slate-200 rounded-lg">
                          <SelectValue placeholder={petType === 'dog' ? t('form.select_dog_breed') : t('form.select_cat_breed')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(petType === 'dog' ? DOG_BREEDS : CAT_BREEDS).map((breed) => (
                            <SelectItem key={breed.slug} value={breed.slug}>
                              {breed.name}
                              {breed.category === 'brachycephalic' && (
                                <span className="ml-2 text-red-500 text-xs">⚠️</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {petBreed && (petType === 'dog' ? DOG_BREEDS : CAT_BREEDS).find(b => b.slug === petBreed)?.category === 'brachycephalic' && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-700 text-sm">
                            <strong>⚠️</strong> {t('form.breed_warning')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    data-testid="next-step-1-btn"
                    onClick={handleNext}
                    className="w-full bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full h-12 font-medium transition-colors mt-4"
                  >
                    {t('form.continue')}
                  </Button>
                </motion.div>
              )}

              {/* Step 2: Plan Selection */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <div className="grid gap-4">
                    {/* Plan $39 */}
                    <div
                      data-testid="plan-39-card"
                      onClick={() => setSelectedPlan('$39')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        selectedPlan === '$39'
                          ? 'border-[#0066CC] bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900">{t('plans.cage_check')}</h3>
                          <p className="text-2xl font-bold text-[#0066CC]">$39</p>
                          <p className="text-xs text-slate-500">{t('plans.cage_check_tagline')}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPlan === '$39' ? 'border-[#0066CC] bg-[#0066CC]' : 'border-slate-300'
                        }`}>
                          {selectedPlan === '$39' && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                      <ul className="space-y-1 text-sm text-slate-600">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_crate_analysis')}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_iata_check')}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_airline_rules_60')}
                        </li>
                      </ul>
                    </div>

                    {/* Plan $89 */}
                    <div
                      data-testid="plan-89-card"
                      onClick={() => setSelectedPlan('$89')}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-colors relative ${
                        selectedPlan === '$89'
                          ? 'border-[#0066CC] bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3 mt-1">
                        <div>
                          <h3 className="font-semibold text-slate-900">{t('plans.full_audit')}</h3>
                          <p className="text-2xl font-bold text-[#0066CC]">$89</p>
                          <p className="text-xs text-slate-500">{t('plans.full_audit_tagline')}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPlan === '$89' ? 'border-[#0066CC] bg-[#0066CC]' : 'border-slate-300'
                        }`}>
                          {selectedPlan === '$89' && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                      <ul className="space-y-1 text-sm text-slate-600">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_crate_analysis')}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_document_check')}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_country_rules')}
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {t('plans.feature_pdf_report')}
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Subtle upsell when $39 is selected */}
                  {selectedPlan === '$39' && (
                    <div 
                      onClick={() => setSelectedPlan('$89')}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200/60 cursor-pointer hover:border-amber-300 transition-colors"
                    >
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <Shield className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-amber-800">
                          <span className="font-medium">{t('upsell.flying_international')}</span> {t('upsell.document_recommended')}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-amber-700">+$50 →</span>
                    </div>
                  )}

                  {/* Refund Guarantee - premium style */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/60 rounded-lg p-3 mt-4">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-green-800 mb-1">
                          {t('guarantee.title')}
                        </p>
                        <p className="text-[10px] text-green-700 leading-relaxed">
                          {t('guarantee.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick info line */}
                  <div className="text-center py-2">
                    <p className="text-[10px] text-slate-400">
                      {t('guarantee.quick_info')}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      data-testid="back-step-2-btn"
                      onClick={handleBack}
                      variant="outline"
                      className="flex-1 rounded-full h-12"
                    >
                      {t('form.back')}
                    </Button>
                    <Button
                      data-testid="next-step-2-btn"
                      onClick={handleNext}
                      disabled={!selectedPlan}
                      className="flex-1 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full h-12"
                    >
                      {t('form.continue')}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Upload */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <MeasurementsForm value={measurements} onChange={setMeasurements} />

                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
                    <p className="font-medium text-blue-900 mb-1">📸 {t('upload.subtitle')}</p>
                    <p className="text-blue-700 text-xs">{t('upload.title')}</p>
                  </div>

                  {/* Visual guide for photos - Custom Icons with Upload Status */}
                  <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                      <span className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-[10px]">📷</span>
                      {t('upload.what_to_photo')}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {/* Icon 1: Pet in Crate */}
                      <div className={`relative bg-white rounded-xl p-3 border shadow-sm transition-all text-center ${
                        photoA ? 'border-green-400 bg-green-50/50' : 'border-slate-100 hover:shadow-md hover:border-blue-200'
                      }`}>
                        {photoA && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_64897331-93bb-49a9-9089-bc45b7319e45/artifacts/ng0c4im0_30000BE7-4899-4B98-BA89-393B9A4633B7.png" 
                            alt="Pet in crate" 
                            className={`w-full h-full object-contain ${photoA ? 'opacity-60' : ''}`}
                          />
                        </div>
                        <span className={`text-xs font-medium ${photoA ? 'text-green-700' : 'text-slate-700'}`}>
                          {photoA ? `✓ ${t('upload.uploaded')}` : t('upload.pet_in_crate')}
                        </span>
                      </div>
                      
                      {/* Icon 2: Full Crate */}
                      <div className={`relative bg-white rounded-xl p-3 border shadow-sm transition-all text-center ${
                        photoB ? 'border-green-400 bg-green-50/50' : 'border-slate-100 hover:shadow-md hover:border-blue-200'
                      }`}>
                        {photoB && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_64897331-93bb-49a9-9089-bc45b7319e45/artifacts/dlvetmly_0970DF0B-6775-4A71-8A3A-F82FED407AB6.png" 
                            alt="Full crate view" 
                            className={`w-full h-full object-contain ${photoB ? 'opacity-60' : ''}`}
                          />
                        </div>
                        <span className={`text-xs font-medium ${photoB ? 'text-green-700' : 'text-slate-700'}`}>
                          {photoB ? `✓ ${t('upload.uploaded')}` : t('upload.full_crate')}
                        </span>
                      </div>
                      
                      {/* Icon 3: Door & Locks */}
                      <div className={`relative bg-white rounded-xl p-3 border shadow-sm transition-all text-center ${
                        photoC ? 'border-green-400 bg-green-50/50' : 'border-slate-100 hover:shadow-md hover:border-blue-200'
                      }`}>
                        {photoC && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_64897331-93bb-49a9-9089-bc45b7319e45/artifacts/qg786pik_1E8834D7-295C-48DE-AF8E-A67190B66AE0.png" 
                            alt="Door and locks" 
                            className={`w-full h-full object-contain ${photoC ? 'opacity-60' : ''}`}
                          />
                        </div>
                        <span className={`text-xs font-medium ${photoC ? 'text-green-700' : 'text-slate-700'}`}>
                          {photoC ? `✓ ${t('upload.uploaded')}` : t('upload.door_locks')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Progress indicator */}
                    {(photoA || photoB || photoC) && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">{t('upload.upload_progress')}</span>
                          <span className={`font-semibold ${photoA && photoB && photoC ? 'text-green-600' : 'text-blue-600'}`}>
                            {[photoA, photoB, photoC].filter(Boolean).length}/3 {t('upload.photos')}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              photoA && photoB && photoC ? 'bg-green-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${([photoA, photoB, photoC].filter(Boolean).length / 3) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <FileUploadZone 
                      file={photoA} 
                      setFile={setPhotoA} 
                      label={t('upload.photo1_label')} 
                      description={t('upload.photo1_hint')} 
                    />
                    <FileUploadZone 
                      file={photoB} 
                      setFile={setPhotoB} 
                      label={t('upload.photo2_label')} 
                      description={t('upload.photo2_hint')} 
                    />
                    <FileUploadZone 
                      file={photoC} 
                      setFile={setPhotoC} 
                      label={t('upload.photo3_label')} 
                      description={t('upload.photo3_hint')} 
                    />
                  </div>

                  {selectedPlan === '$89' && (
                    <div className="pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">📄 {t('upload.documents_title')} ({t('upload.documents_subtitle')})</p>
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">{t('upload.higher_accuracy')}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">{t('upload.documents_desc')}</p>
                      <div className="space-y-3">
                        <FileUploadZone file={docPassport} setFile={setDocPassport} label={t('upload.doc_passport')} description={t('upload.doc_passport_hint')} />
                        <FileUploadZone file={docRabies} setFile={setDocRabies} label={t('upload.doc_rabies')} description={t('upload.doc_rabies_hint')} />
                        <FileUploadZone file={docHealth} setFile={setDocHealth} label={t('upload.doc_health')} description={t('upload.doc_health_hint')} />
                      </div>
                    </div>
                  )}

                  {/* Progress indicator */}
                  <div className="text-center py-2">
                    <p className="text-xs text-slate-500">
                      {photoA && photoB && photoC 
                        ? `✅ ${t('upload.all_photos_ready')}` 
                        : `📷 ${[photoA, photoB, photoC].filter(Boolean).length}/3 ${t('upload.photos_uploaded')}`}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button data-testid="back-step-3-btn" onClick={handleBack} variant="outline" className="flex-1 rounded-full h-12">
                      {t('form.back')}
                    </Button>
                    <Button data-testid="next-step-3-btn" onClick={handleNext} className="flex-1 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full h-12">
                      {t('form.continue')}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Payment */}
              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  {/* Urgency message based on flight date */}
                  {flightDate && (() => {
                    const daysUntilFlight = Math.ceil((new Date(flightDate) - new Date()) / (1000 * 60 * 60 * 24));
                    if (daysUntilFlight <= 3) {
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                          <span className="text-red-500">⚠️</span>
                          <p className="text-xs text-red-800">
                            <strong>{daysUntilFlight === 1 ? t('urgency.flight_urgent', { days: daysUntilFlight }) : t('urgency.flight_urgent_plural', { days: daysUntilFlight })}</strong>
                          </p>
                        </div>
                      );
                    } else if (daysUntilFlight <= 7) {
                      return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                          <span className="text-amber-500">⏰</span>
                          <p className="text-xs text-amber-800">
                            <strong>{t('urgency.flight_soon', { days: daysUntilFlight })}</strong>
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t('payment.plan')}</span>
                      <span className="font-medium">{selectedPlan === '$39' ? t('plans.cage_check') : t('plans.full_audit')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t('payment.photos_count')}</span>
                      <span className="font-medium">{t('payment.photos_uploaded_count')}</span>
                    </div>
                    {selectedPlan === '$89' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{t('payment.documents')}</span>
                        <span className="font-medium">{[docPassport, docRabies, docHealth].filter(Boolean).length} {t('upload.uploaded')}</span>
                      </div>
                    )}
                    <div className="border-t border-slate-200 pt-2 mt-2">
                      <div className="flex justify-between font-bold">
                        <span>{t('payment.total')}</span>
                        <span className="text-[#0066CC]">{selectedPlan}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1 block">
                      {t('payment.your_email')} *
                    </Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder={t('payment.email_placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 bg-slate-50 border-slate-200 rounded-lg"
                    />
                    <p className="text-xs text-slate-500 mt-1">{t('payment.email_note')}</p>
                  </div>

                  {/* Trust Badge - Positive Message */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p className="text-xs text-green-800">
                      <strong>{t('payment.iata_certified')}</strong> {t('payment.iata_description')}
                    </p>
                  </div>

                  {/* Payment Verification Message */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-blue-800">
                      <strong>{t('payment.before_paying')}</strong> {t('payment.payment_tip')}
                    </p>
                  </div>

                  {/* Secure Payment Badge */}
                  <div className="flex flex-col items-center justify-center py-4 space-y-3">
                    <div className="flex items-center gap-2 text-slate-600">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold">{t('payment.secure_payment')}</span>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap justify-center">
                      {/* Apple Pay - Official mark */}
                      <div className="h-8 px-3 bg-black rounded-md flex items-center justify-center">
                        <svg className="h-5" viewBox="0 0 165.521 105.965" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#ffffff" d="M150.698 0H14.823C6.631 0 0 6.631 0 14.823v76.319c0 8.192 6.631 14.823 14.823 14.823h135.875c8.192 0 14.823-6.631 14.823-14.823V14.823C165.521 6.631 158.89 0 150.698 0z"/>
                          <path d="M150.698 3.532c6.229 0 11.291 5.062 11.291 11.291v76.319c0 6.229-5.062 11.291-11.291 11.291H14.823c-6.229 0-11.291-5.062-11.291-11.291V14.823c0-6.229 5.062-11.291 11.291-11.291h135.875m0-3.532H14.823C6.631 0 0 6.631 0 14.823v76.319c0 8.192 6.631 14.823 14.823 14.823h135.875c8.192 0 14.823-6.631 14.823-14.823V14.823C165.521 6.631 158.89 0 150.698 0z"/>
                          <path fill="#000" d="M43.508 35.77c1.404-1.755 2.356-4.112 2.105-6.52-2.054.102-4.56 1.355-6.012 3.112-1.303 1.504-2.456 3.959-2.156 6.266 2.306.2 4.61-1.152 6.063-2.858zM45.587 39.079c-3.35-.2-6.196 1.9-7.795 1.9-1.6 0-4.049-1.8-6.698-1.751-3.449.05-6.648 2-8.398 5.1-3.6 6.2-.95 15.4 2.55 20.45 1.699 2.5 3.747 5.25 6.445 5.151 2.55-.1 3.549-1.65 6.647-1.65 3.097 0 3.997 1.65 6.696 1.6 2.798-.05 4.548-2.5 6.247-5 1.95-2.85 2.747-5.6 2.797-5.75-.05-.05-5.396-2.101-5.446-8.251-.05-5.15 4.198-7.6 4.398-7.751-2.399-3.548-6.147-3.948-7.447-4.048zM78.973 32.11c7.278 0 12.347 5.017 12.347 12.321 0 7.33-5.173 12.373-12.529 12.373h-8.058v12.806h-6.117V32.11h14.357zm-8.24 19.807h6.68c5.07 0 7.954-2.729 7.954-7.46 0-4.73-2.885-7.434-7.928-7.434h-6.706v14.894zM93.726 59.115c0-4.782 3.663-7.72 10.156-8.076l7.486-.433v-2.104c0-3.04-2.052-4.86-5.484-4.86-3.25 0-5.25 1.665-5.74 4.213h-5.586c.334-5.2 4.653-9.022 11.508-9.022 6.754 0 11.065 3.587 11.065 9.178v19.21h-5.664v-4.576h-.13c-1.664 3.118-5.3 5.04-9.07 5.04-5.664 0-9.541-3.457-9.541-8.57zm17.642-2.572v-2.157l-6.728.408c-3.38.23-5.302 1.846-5.302 4.394 0 2.599 1.976 4.264 5.019 4.264 3.977 0 7.011-2.729 7.011-6.909zM123.621 80.212v-4.783c.44.104 1.43.104 1.924.104 2.754 0 4.24-1.154 5.147-4.135l.543-1.768-10.416-28.326h6.4l7.278 22.899h.104l7.278-22.899h6.244l-10.832 29.896c-2.47 6.936-5.329 9.178-11.326 9.178-.494 0-1.82-.052-2.344-.156z"/>
                        </svg>
                      </div>
                      {/* Google Pay - Official */}
                      <div className="h-8 px-3 bg-white border border-slate-200 rounded-md flex items-center justify-center">
                        <svg className="h-5" viewBox="0 0 435.97 173.13" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#5F6368" d="M206.2,84.58v50.75H190.1V10h42.7a38.61,38.61,0,0,1,27.65,10.85A34.88,34.88,0,0,1,272,47.3a34.72,34.72,0,0,1-11.55,26.6q-11.2,10.68-27.65,10.67H206.2Zm0-59.15V69.27h27a21.28,21.28,0,0,0,15.93-6.48,21.36,21.36,0,0,0,0-30.63,21,21,0,0,0-15.93-6.72h-27Z"/>
                          <path fill="#5F6368" d="M309.1,46.78q17.85,0,28.18,9.54T347.6,82.48v52.85H332.05v-11.9h-.7q-10,14.7-26.6,14.7-14.17,0-23.71-8.4a26.82,26.82,0,0,1-9.54-21q0-13.3,10.06-21.17t26.86-7.88q14.34,0,23.62,5.25V81.08A18.33,18.33,0,0,0,325.54,67,22.8,22.8,0,0,0,310,61.13q-13.66,0-21.52,11.55l-14-8.75Q285.73,46.78,309.1,46.78Zm-20.83,62.3a12.86,12.86,0,0,0,5.34,10.5,19.64,19.64,0,0,0,12.51,4.2,25.67,25.67,0,0,0,18.11-7.52q8-7.53,8-17.67-7.53-6-21.35-6-10.07,0-16.36,4.73C290.07,100.47,288.27,104.56,288.27,109.08Z"/>
                          <path fill="#5F6368" d="M436,49.58,382.24,173.13H365.62l19.95-43.23L350,49.58h17.5l25.55,61.6h.35l24.85-61.6Z"/>
                          <path fill="#4285F4" d="M141.14,73.64A85.79,85.79,0,0,0,139.9,59H72.09V86.73h38.89a33.33,33.33,0,0,1-14.41,21.84v17.9h23.21C133.31,114,141.14,95.2,141.14,73.64Z"/>
                          <path fill="#34A853" d="M72.09,143.3c19.43,0,35.79-6.38,47.72-17.32l-23.21-17.9c-6.47,4.37-14.76,6.88-24.51,6.88-18.78,0-34.72-12.66-40.42-29.72H7.67v18.46A72,72,0,0,0,72.09,143.3Z"/>
                          <path fill="#FBBC04" d="M31.67,85.24a43.38,43.38,0,0,1,0-27.6V39.18H7.67a72.25,72.25,0,0,0,0,64.52Z"/>
                          <path fill="#EA4335" d="M72.09,27.92A39,39,0,0,1,99.6,38.63l20.47-20.35A68.93,68.93,0,0,0,72.09,0,72,72,0,0,0,7.67,39.18l24,18.46C37.37,40.58,53.31,27.92,72.09,27.92Z"/>
                        </svg>
                      </div>
                      {/* Visa - Official blue */}
                      <svg className="h-8" viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#1434CB" d="M293.2 348.73l33.36-195.76h53.34l-33.36 195.76H293.2zm246.11-191.54c-10.57-3.97-27.14-8.22-47.82-8.22-52.73 0-89.88 26.6-90.17 64.67-.29 28.16 26.48 43.87 46.71 53.25 20.79 9.6 27.77 15.73 27.67 24.31-.14 13.13-16.58 19.12-31.93 19.12-21.37 0-32.71-2.97-50.25-10.28l-6.88-3.11-7.49 43.89c12.48 5.48 35.54 10.22 59.5 10.46 56.07 0 92.47-26.27 92.89-66.96.21-22.3-14.01-39.27-44.79-53.26-18.65-9.07-30.07-15.12-29.94-24.3 0-8.14 9.66-16.85 30.53-16.85 17.43-.28 30.07 3.54 39.94 7.51l4.78 2.26 7.25-42.49zm137.95-4.22h-41.23c-12.77 0-22.33 3.49-27.94 16.26l-79.27 179.5h56.04s9.16-24.14 11.23-29.44l68.33.08c1.6 6.87 6.49 29.36 6.49 29.36h49.54l-43.19-195.76zm-65.63 126.41c4.41-11.28 21.26-54.71 21.26-54.71-.31.52 4.38-11.33 7.07-18.69l3.61 16.88s10.21 46.72 12.35 56.52h-44.29zM246.8 152.97l-52.24 133.52-5.59-27.18c-9.72-31.3-40.01-65.24-73.92-82.25l47.77 171.6 56.47-.06 84.03-195.63h-56.52"/>
                        <path fill="#F9A533" d="M146.92 152.96H60.88l-.68 4.01c66.94 16.21 111.21 55.36 129.56 102.39l-18.69-89.88c-3.22-12.36-12.59-16.05-24.15-16.52"/>
                      </svg>
                      {/* Mastercard - Official */}
                      <svg className="h-8" viewBox="0 0 131.39 86.9" xmlns="http://www.w3.org/2000/svg">
                        <rect width="131.39" height="86.9" fill="none"/>
                        <circle cx="43.45" cy="43.45" r="43.45" fill="#EB001B"/>
                        <circle cx="87.94" cy="43.45" r="43.45" fill="#F79E1B"/>
                        <path fill="#FF5F00" d="M65.7,11.35a43.35,43.35,0,0,0,0,64.2,43.35,43.35,0,0,0,0-64.2Z"/>
                      </svg>
                      {/* Stripe - Powered by */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-400 font-medium">{t('payment.powered_by')}</span>
                        <img 
                          src="/assets/stripe-logo.svg" 
                          alt="Stripe" 
                          className="h-5"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 text-center">{t('payment.ssl_note')}</p>
                  </div>

                  {/* Refund Guarantee Reminder - at checkout */}
                  <div className="flex items-center justify-center gap-2 py-2 bg-green-50/50 rounded-lg border border-green-100">
                    <Shield className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-[10px] text-green-700">
                      {t('guarantee.short')}
                    </span>
                  </div>

                  <div data-testid="checkout-reassurance" className="grid sm:grid-cols-3 gap-3 text-xs text-slate-700">
                    <div className="bg-slate-50 rounded-lg p-3"><strong className="block text-slate-900">Secure payment by Stripe</strong>Card, Apple Pay, Google Pay or Link. We never see your card details.</div>
                    <div className="bg-slate-50 rounded-lg p-3"><strong className="block text-slate-900">PDF report usually in a few minutes</strong>Shown on screen, with a PDF download.</div>
                    <div className="bg-slate-50 rounded-lg p-3"><strong className="block text-slate-900">Refund guarantee</strong>Full refund if we miss an IATA requirement verifiable from your photos and measurements.</div>
                  </div>
                  <p className="text-[11px] text-slate-500 text-center">Independent service, not affiliated with IATA or any airline. The airline makes the final decision.</p>

                  <div className="flex gap-3">
                    <Button data-testid="back-step-4-btn" onClick={handleBack} variant="outline" className="flex-1 rounded-full h-12" disabled={loading}>
                      {t('form.back')}
                    </Button>
                    <Button
                      data-testid="submit-payment-btn"
                      onClick={handleSubmit}
                      disabled={loading || !email}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full h-12 font-semibold"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('payment.processing')}
                        </>
                      ) : (
                        <>
                          🛡️ {t('payment.get_report')}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Final reassurance */}
                  <p className="text-xs text-center text-slate-400 mt-3">
                    {t('payment.final_note')}
                  </p>
                  <p data-testid="checkout-independent-disclaimer" className="text-xs text-center text-slate-500 mt-1">
                    {t('disclaimer.independent')}
                  </p>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sample Report Preview - Only on Step 1 */}
      {step === 1 && (
        <SamplePDFPreview 
          onCtaClick={() => {
            if (wizardRef.current) {
              wizardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />
      )}

      {/* SEO Resources Section - Only on Step 1 */}
      {step === 1 && (
      <section className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('resources.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('resources.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Airlines Card */}
            <a href="/airlines" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-slate-200 hover:border-blue-300 group">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                <Plane className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('resources.airlines')}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('resources.airlines_subtitle')}
              </p>
              <ul className="text-sm text-slate-500 space-y-1 mb-4">
                <li>• {t('resources.airlines_list_1')}</li>
                <li>• {t('resources.airlines_list_2')}</li>
                <li>• {t('resources.airlines_list_3')}</li>
              </ul>
              <span className="text-blue-600 font-medium text-sm group-hover:text-blue-700">
                {t('resources.view_airlines')}
              </span>
            </a>

            {/* Destinations Card */}
            <a href="/destinations" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-slate-200 hover:border-green-300 group">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('resources.destinations')}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('resources.destinations_subtitle')}
              </p>
              <ul className="text-sm text-slate-500 space-y-1 mb-4">
                <li>• {t('resources.destinations_list_1')}</li>
                <li>• {t('resources.destinations_list_2')}</li>
                <li>• {t('resources.destinations_list_3')}</li>
              </ul>
              <span className="text-green-600 font-medium text-sm group-hover:text-green-700">
                {t('resources.view_destinations')}
              </span>
            </a>

            {/* Breeds Card */}
            <a href="/breeds" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-slate-200 hover:border-red-300 group">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-200 transition-colors">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('resources.breeds')}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('resources.breeds_subtitle')}
              </p>
              <ul className="text-sm text-slate-500 space-y-1 mb-4">
                <li>• {t('resources.breeds_list_1')}</li>
                <li>• {t('resources.breeds_list_2')}</li>
                <li>• {t('resources.breeds_list_3')}</li>
              </ul>
              <span className="text-red-600 font-medium text-sm group-hover:text-red-700">
                {t('resources.view_breeds')}
              </span>
            </a>

            {/* Guides Card */}
            <a href="/guides" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-slate-200 hover:border-amber-300 group">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('resources.guides')}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('resources.guides_subtitle')}
              </p>
              <ul className="text-sm text-slate-500 space-y-1 mb-4">
                <li>• {t('resources.guides_list_1')}</li>
                <li>• {t('resources.guides_list_2')}</li>
                <li>• {t('resources.guides_list_3')}</li>
              </ul>
              <span className="text-amber-600 font-medium text-sm group-hover:text-amber-700">
                {t('resources.view_guides')}
              </span>
            </a>
          </div>

          {/* Popular Articles */}
          <div className="mt-12 bg-white rounded-xl p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-6">{t('resources.popular_articles')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <a href="/guides/why-airlines-reject-crates" className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-2xl">❌</span>
                <div>
                  <h4 className="font-semibold text-slate-900">{t('resources.article1_title')}</h4>
                  <p className="text-sm text-slate-600">{t('resources.article1_desc')}</p>
                </div>
              </a>
              <a href="/guides/iata-crate-size-calculator" className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-2xl">📏</span>
                <div>
                  <h4 className="font-semibold text-slate-900">{t('resources.article2_title')}</h4>
                  <p className="text-sm text-slate-600">{t('resources.article2_desc')}</p>
                </div>
              </a>
              <a href="/guides/metal-bolts-vs-plastic-clips" className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-2xl">🔩</span>
                <div>
                  <h4 className="font-semibold text-slate-900">{t('resources.article3_title')}</h4>
                  <p className="text-sm text-slate-600">{t('resources.article3_desc')}</p>
                </div>
              </a>
              <a href="/breeds/french-bulldog" className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-2xl">🐕</span>
                <div>
                  <h4 className="font-semibold text-slate-900">{t('resources.article4_title')}</h4>
                  <p className="text-sm text-slate-600">{t('resources.article4_desc')}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Reviews: only rendered when 3+ reviews linked to a real Stripe payment exist */}
      {step === 1 && <ReviewsCarousel />}

      {/* FAQ Section - Only on Step 1 */}
      {step === 1 && (i18n.resolvedLanguage === 'fr' ? <FAQSection /> : (
        <CopySections onStart={(plan) => { setSelectedPlan(plan); wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />
      ))}

      {/* Sticky CTA for Mobile */}
      {step === 1 && (
        <StickyCTAMobile 
          onCtaClick={() => {
            if (wizardRef.current) {
              wizardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <img src="/logo.png" alt="PetFlyCheck" className="w-16 h-16 md:w-20 md:h-20 object-contain" />
                <span className="font-bold text-2xl md:text-3xl">PetFlyCheck</span>
              </div>
              <p className="text-base text-slate-400">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('footer.resources')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/airlines" className="hover:text-white transition-colors">{t('footer.airline_policies')}</a></li>
                <li><a href="/destinations" className="hover:text-white transition-colors">{t('footer.country_requirements')}</a></li>
                <li><a href="/breeds" className="hover:text-white transition-colors">{t('footer.breed_guides')}</a></li>
                <li><a href="/guides" className="hover:text-white transition-colors">{t('footer.travel_guides')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/business" className="hover:text-white transition-colors font-medium text-blue-400">{t('footer.for_business')}</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors">{t('nav.faq')}</a></li>
                <li><a href="/support" className="hover:text-white transition-colors">{t('nav.support')}</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('footer.contact')}</h4>
              <a 
                href="mailto:contact@petflycheck.io?subject=Question about PetFlyCheck"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = "mailto:contact@petflycheck.io?subject=Question about PetFlyCheck";
                }}
                className="text-sm text-slate-400 hover:text-white transition-colors underline cursor-pointer"
                data-testid="contact-email-link"
              >
                contact@petflycheck.io
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            {t('footer.copyright')}
            <p data-testid="footer-disclaimer" className="mt-3 text-xs max-w-4xl mx-auto">{FOOTER_DISCLAIMER}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
